import React, {useState} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import {Button} from '@/components/ui';
import Icon from '@/components/icons/Icon';
import Spinner from '@/components/ui/Spinner';

import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import {navigate} from '@reach/router';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import {buildAgentDashboardURL} from '@/utils/urlBuilder';
import usePaginatedData from '@/utils/usePaginatedData';
import AgentSettingsModal from './AgentSettingsModal';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useData from '@/utils/useData';
import {ROUTES} from '@/routing/route';
import AgentAssignUserModal from './AgentAssignUserModal';
import TableComponent from '@/components/table/Table';
import ClickableTableCell from '@/components/table/ClickableTableCell';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {useStoreActions} from '@/store';
import {RestrictedFieldIndicator} from '@/components/utils/DataNotAvailable';

interface User {
    id: number;
    username: string;
    email: string;
}

interface Agent {
    id: number;
    fullName: string;
    name: string;
    externalId: string;
    updatedOn: string;
    createdOn: string;
    user: User | null;
    teamName: string | null;
}

interface Story {
    id: number;
    assignedUrl: string;
    firstStoryboardHashid: string;
    firstStoryboardSlug: string;
}

interface UserData {
    permissions?: string[];
}

const setSearchQueryState = async (
    setSearchQuery: (value: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const AgentListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [assignAgentModalOpen, setAssignAgentModalOpen] = useState<boolean>(false);
    const [selectedAgentId, setSelectedAgentId] = useState<number>(0);
    const [selectedAgentName, setSelectedAgentName] = useState<string>('');

    const [searchQuery, setSearchQuery] = useState<string>('');

    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const setAgentsReload = useStoreActions((actions) => actions.agents.setReload);

    const canCreateAgent = hasPermission('fs.agent.create', userData?.permissions, true);
    const canChangeAgent = hasPermission('fs.agent.update', userData?.permissions, true);
    const canAssignAgent = hasPermission('fs.agent.team_assignment.create', userData?.permissions, true);

    const [personsStories] = useData<Story[]>(window.URLS.storiesResource, {
        storeResolver: 'personsStories',
        initialValue: [],
        requestParams: {
            params: {storyListType: 'personsStories'},
        },
    });

    const {
        data: agents,
        loading: loadingAgents,
        currentPage,
        setCurrentPage,
        pages,
    } = usePaginatedData(window.URLS.agentsResource, {
        storeResolver: 'agents',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    }) as {
        data: Agent[];
        loading: boolean;
        currentPage: number | null;
        setCurrentPage: (page: number) => void;
        pages: number[] | null;
    };

    const userCanAccessSpecialStoryViewObject = hasPermission('fs.story.list', userData?.permissions, true) || false;

    const userCanAccessAgentsResource = hasPermission('fs.agent.list', userData?.permissions, true) || false;

    if (loadingUserData) {
        return (
            <Spinner
                size="md"
                text={i18n.t('jt_loading_data_general.text')}
                centered
                fullHeight
            />
        );
    }

    if (!userCanAccessAgentsResource) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_here_you.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openSettingsModal = (agentId: number): void => {
        setSelectedAgentId(agentId);
        setSettingsModalOpen(true);
    };

    const openAssignAgentModal = (agentId: number, agentName: string): void => {
        setSelectedAgentId(agentId);
        setSelectedAgentName(agentName);
        setAssignAgentModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setSelectedAgentId(0);
        setAgentsReload(true);
    };

    const closeAssignAgentModal = (): void => {
        setAssignAgentModalOpen(false);
        setSelectedAgentId(0);
    };


    let url = '#';
    let storyType = 'template';
    personsStories.forEach((story: Story) => {
        if (story.assignedUrl === 'agents') {
            if (story.id > 0) {
                url = ROUTES.storyboardViewRoute.replace(
                    ':activeStoryboardHashid', story.firstStoryboardHashid,
                ).replace(
                    ':activeStoryboardUrl', story.firstStoryboardSlug,
                );
                storyType = 'special';
            } else {
                url = ROUTES.storyboardTemplateViewRoute.replace(
                    ':activeStoryUrl', story.assignedUrl,
                ).replace(
                    ':activeStoryboardUrl', story.firstStoryboardSlug,
                );
            }
        }
    });

    const detailUrlGenerator = (rowData: Agent): string => {
        return buildAgentDashboardURL(rowData.id);
    };

    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const agentPiiRestricted = restrictedFieldKeys.includes('agent_pii');
    const teamNameRestricted = restrictedFieldKeys.includes('team_pii');

    const columns = [
        {
            header: i18n.t('th_name.text'),
            accessorKey: 'fullName',
            cell: (cell: any) => {
                if (agentPiiRestricted) {
                    return <RestrictedFieldIndicator />;
                }
                return <ClickableTableCell cell={cell} urlGenerator={detailUrlGenerator} />;
            },
        },
        {
            header: i18n.t('th_external.text'),
            accessorKey: 'externalId',
            cell: (cell: any) => {
                if (agentPiiRestricted) {
                    return <RestrictedFieldIndicator />;
                }
                return cell.getValue() || '';
            },
        },
        {
            id: 'i18ntthlastmodified10text',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: Agent) => {
                if (!cell.updatedOn || cell.updatedOn === '-') {
                    return configLoaded ? formatDateTime(cell.createdOn, true, {setZone: false},
                    ) : '';
                }

                return configLoaded ? formatDateTime(cell.updatedOn, true, {setZone: false},
                ) : '';
            },
        },
        // User assignment is not yet implemented
        // {
        //     header: i18n.t('th_has_user.text'),
        //     accessorKey: 'user.id',
        //     cell: (cell: any) => {
        //         const user = cell.row.original.user;

        //         return user && user.id
        //             ? `${user.username} (${user.email})`
        //             : <Icon name="mdi-close" className="text-danger" />;
        //     },
        // },
        {
            header: i18n.t('th_team.text'),
            accessorKey: 'teamName',
            cell: (cell: any) => {
                if (teamNameRestricted) {
                    return <RestrictedFieldIndicator />;
                }
                return cell.getValue() ? cell.getValue() : <Icon name="mdi-close" className="text-danger" />;
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <div className="d-flex align-items-center justify-content-end w-100">
                    <div className="d-inline-block mr-2">
                        {
                            userCanAccessSpecialStoryViewObject ? (
                                <Tooltip content={i18n.t('jt_view.text')} placement="top">
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        icon="mdi-eye"
                                        data-cy="agent-view-button"
                                        onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                                    />
                                </Tooltip>
                            ) : (
                                <Tooltip
                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                    placement="top"
                                >
                                    <div style={{
                                        display: 'inline-block',
                                        cursor: 'not-allowed',
                                    }}
                                    >
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            icon="mdi-eye"
                                            disabled
                                            style={{pointerEvents: 'none'}}
                                            data-cy="agent-list-eye-outlinesecondary-btn" />
                                    </div>
                                </Tooltip>
                            )
                        }
                    </div>
                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                        <Dropdown className="d-inline-block">
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-dots-horizontal"
                                className="dropdown-toggle-no-caret"
                                data-cy="agent-settings-toggle"
                                id="settings-dropdown-1"
                            />

                            <Dropdown.Menu align="end">
                                {
                                    canChangeAgent ? (
                                        <Dropdown.Item
                                            onClick={() => openSettingsModal(cell.row.original.id)}
                                            data-cy="agent-settings-button"
                                        >
                                            <Icon name="mdi-settings" className="mr-2" />  {i18n.t('jt_settings.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item
                                                    style={{pointerEvents: 'none'}}
                                                    disabled
                                                    data-cy="agent-list-btn-2">
                                                    <Icon name="mdi-settings" className="mr-2" />  {i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }

                                {/* Agent assignment is not yet implemented */}
                                {/* {
                                    canAssignAgent ? (
                                        <Dropdown.Item
                                            onClick={() => openAssignAgentModal(cell.row.original.id, cell.row.original.name)}
                                            data-cy="agent-assign-user-button"
                                        >
                                            <Icon name="mdi-account-plus-outline" className="mr-2" /> {i18n.t('jt_assign_to_agent_to_user.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item
                                                    style={{pointerEvents: 'none'}}
                                                    disabled
                                                    data-cy="agent-list-btn">
                                                    <Icon name="mdi-account-plus-outline" className="mr-2" /> {i18n.t('jt_assign_to_agent_to_user.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                } */}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_here_you.text')} />

            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            data-cy="agent-search-field"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end align-items-center">
                    {
                        canCreateAgent ? (
                            <Button
                                variant="info"
                                size="sm"
                                className='mb-2'
                                icon="mdi-plus"
                                onClick={() => setSettingsModalOpen(true)}
                                data-cy="new-agent"
                            >
                                {i18n.t('bn_add_new_agent.text')}
                            </Button>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div style={{cursor: 'not-allowed', display: 'inline-block'}}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon="mdi-plus"
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                        data-cy="agent-list-plus-btn"
                                    >
                                        {i18n.t('bn_add_new_agent.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    }
                </Col>
            </Row>

            <LoadingWrapper
                loading={loadingAgents}
                loadingText={i18n.t('jt_loading_data_general.text')}
                minHeight="400px"
            >
                <div className="card">
                    <div className="card-body">
                        <TableComponent data={agents} columns={columns} />
                        <PaginationComponent
                            pages={pages}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            hideWhenSinglePage={false}
                        />
                    </div>
                </div>

                {
                    settingsModalOpen ? (
                        <AgentSettingsModal
                            modalOpen={settingsModalOpen}
                            closeModal={closeSettingsModal}
                            activeAgentId={selectedAgentId}
                        />
                    ) : null
                }

                {/* Agent assignment is not yet implemented */}
                {/* {
                    assignAgentModalOpen ? (
                        <AgentAssignUserModal
                            modalOpen={assignAgentModalOpen}
                            closeModal={closeAssignAgentModal}
                            activeAgentId={selectedAgentId}
                            activeAgentName={selectedAgentName}
                        />
                    ) : null
                } */}
            </LoadingWrapper>
        </>
    );
});

export default AgentListView; 